import React, { useEffect, useMemo, useState } from "react";
import Axios from "axios";
import "./custom.css";
import 'react-toastify/dist/ReactToastify.css'
import { toast } from "react-toastify";
import {
    Grid,
    Card,
    CardContent,
    Typography,
    CardActions, Avatar,
    DialogContent,
    DialogContentText,
    DialogActions,
    Chip,
    Tooltip,
    CircularProgress,
    IconButton,
} from "@material-ui/core/";
import BusinessIcon from "@material-ui/icons/Business";
import StorageIcon from "@material-ui/icons/Storage";
import RoomIcon from "@material-ui/icons/Room";
import PersonIcon from "@material-ui/icons/Person";
import EventIcon from "@material-ui/icons/Event";
import AssignmentIcon from "@material-ui/icons/Assignment";
import LayersIcon from "@material-ui/icons/Layers";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import { CommonDialog } from "../common/CommonComponents/Dialog";
import { PrimaryButton } from "../common/CommonComponents/Buttons";
const BEDROCK_DETAILS_URL = "https://api-cam.mywizard-aiops.com/bedrock/details/";

// Central place to map a status string to a colour + label so new statuses
// only need one line added here.
const STATUS_STYLES = {
    active: { bg: "#e6f6ec", fg: "#1e7d3c", dot: "#2e9e52" },
    "in-use": { bg: "#e8f0fe", fg: "#1a56c4", dot: "#2f6fed" },
    staging: { bg: "#fff4e0", fg: "#a3610a", dot: "#f0a02c" },
    inactive: { bg: "#f1f1f3", fg: "#63656b", dot: "#9a9ca3" },
};

const getStatusStyle = (status) =>
    STATUS_STYLES[(status || "").toLowerCase()] || STATUS_STYLES.inactive;

const ENVIRONMENT_COLORS = {
    production: "#7500C0",
    staging: "#B05A00",
    development: "#0A7C5C",
};

const InfoRow = ({ icon: Icon, label, value }) => (
    <div className="llm-row">
        <Icon className="llm-row-icon" />
        <div>
            <span className="llm-row-label">{label}</span>
            <span className="llm-row-value">{value || "—"}</span>
        </div>
    </div>
);

// Masks a secret value, keeping the field visually consistent regardless of length
const maskValue = (value) => {
    if (!value) return "—";
    return "•".repeat(Math.min(String(value).length, 20));
};

// Row used for sensitive values (access key / secret key): masked by default,
// with an eye-toggle to reveal and a copy-to-clipboard button.
const SecretDialogRow = ({ label, value }) => {
    const [visible, setVisible] = useState(false);

    const handleCopy = async (e) => {
        e.stopPropagation();
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            toast.success(`${label} copied to clipboard`);
        } catch (err) {
            console.log("Copy failed", err);
            toast.error("Couldn't copy to clipboard");
        }
    };

    return (
        <div className="llm-dialog-row">
            <span className="llm-dialog-label">{label}:</span>
            <span className="llm-dialog-value llm-secret-value">
                {visible ? (value || "—") : maskValue(value)}
                <Tooltip title={visible ? "Hide" : "Show"}>
                    <IconButton
                        size="small"
                        className="llm-secret-icon-btn"
                        onClick={() => setVisible((v) => !v)}
                        disabled={!value}
                    >
                        {visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
                <Tooltip title="Copy">
                    <IconButton
                        size="small"
                        className="llm-secret-icon-btn"
                        onClick={handleCopy}
                        disabled={!value}
                    >
                        <FileCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </span>
        </div>
    );
};

const BedrockAccessCards = (props) => {
    const idToken = localStorage.getItem("token");
    const [open, setOpen] = useState(false);
    const [modalData, setModalData] = useState({});
    const [bedrockData, setBedrockData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const query = props.searchQuery || "";

    const options = {
        method: "GET",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: idToken,
        },
    };

    const getBedrockDetails = () => {
        Axios.get(BEDROCK_DETAILS_URL, options)
            .then((res) => {
                setBedrockData(res.data || []);
                setError(null);
                setLoading(false);
            })
            .catch((err) => {
                console.log("Error fetching Bedrock details", err);
                setError("Couldn't load Bedrock deployments. Please try again shortly.");
                setLoading(false);
            });
    };

    useEffect(() => {
        getBedrockDetails();
        const interval = setInterval(() => {
            getBedrockDetails();
        }, 20000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClose = () => setOpen(false);
    const handleOpen = (data) => {
        setModalData(data);
        setOpen(true);
    };

    const filteredData = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return bedrockData;
        return bedrockData.filter((row) =>
            [row.clientName, row.modelName, row.region, row.environment, row.status, row.wbseCode]
                .filter(Boolean)
                .some((field) => String(field).toLowerCase().includes(q))
        );
    }, [bedrockData, query]);

    if (loading) {
        return (
            <div style={{ marginTop: "10%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress size={40} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="llm-empty-state">
                <b style={{ fontSize: "1.05rem" }}>{error}</b>
            </div>
        );
    }

    return (
        <div style={{ marginBottom: "5%" }}>
            {bedrockData.length > 0 && (
                <div className="llm-toolbar">
                    <span className="llm-result-count">
                        {filteredData.length} of {bedrockData.length} deployment{bedrockData.length !== 1 ? "s" : ""}
                        {query ? ` matching "${query}"` : ""}
                    </span>
                </div>
            )}

            <Grid container spacing={3} direction="row" alignItems="stretch">
                {filteredData.length === 0 ? (
                    <div className="llm-empty-state">
                        <b style={{ fontSize: "1.05rem" }}>
                            {bedrockData.length === 0
                                ? "Looks like you haven’t deployed any Bedrock models."
                                : `No deployments match "${query}".`}
                        </b>
                    </div>
                ) : (
                    filteredData.map((row, key) => {
                        const statusStyle = getStatusStyle(row.status);
                        const envColor = ENVIRONMENT_COLORS[(row.environment || "").toLowerCase()] || "#6b6b76";
                        return (
                            <Grid item xs={12} sm={6} md={4} key={row.inferenceProfileId || key}>
                                <Card variant="outlined" className="llm-card">
                                    <div className="llm-card-header">
                                        <Avatar className="llm-avatar">{(row.clientName || "?").charAt(0)}</Avatar>
                                        <div className="llm-header-text">
                                            <Typography className="llm-client-name" title={row.clientName}>
                                                {row.clientName}
                                            </Typography>
                                            <Typography className="llm-profile-name" title={row.inferenceProfileName}>
                                                {row.inferenceProfileName}
                                            </Typography>
                                        </div>
                                        <Chip
                                            className="llm-status-chip"
                                            style={{ background: statusStyle.bg, color: statusStyle.fg }}
                                            label={
                                                <>
                                                    {row.status}
                                                    <span className="llm-status-dot" style={{ background: statusStyle.dot }} />
                                                </>
                                            }
                                        />
                                    </div>

                                    <CardContent className="llm-body">
                                        <InfoRow icon={LayersIcon} label="Model" value={row.modelName} />
                                        <InfoRow icon={StorageIcon} label="AWS Account ID" value={row.awsAccountId} />
                                        <InfoRow icon={RoomIcon} label="Region" value={`${row.region} · ${row.deploymentRegion}`} />
                                        <div className="llm-row">
                                            <BusinessIcon className="llm-row-icon" />
                                            <div>
                                                <span className="llm-row-label">Environment</span>
                                                <span className="llm-env-pill" style={{ background: envColor }}>
                                                    {row.environment}
                                                </span>
                                            </div>
                                        </div>
                                        <InfoRow icon={PersonIcon} label="Enterprise ID" value={row.enterpriseId} />
                                        <InfoRow icon={AssignmentIcon} label="WBSE Code" value={row.wbseCode} />
                                        <InfoRow icon={EventIcon} label="Created" value={row.createdDate} />
                                    </CardContent>

                                    <CardActions className="llm-card-actions">
                                        <Tooltip title={row.inferenceProfileId}>
                                            <span className="llm-id-text">ID: {row.inferenceProfileId}</span>
                                        </Tooltip>
                                        <span className="llm-show-more-btn" onClick={() => handleOpen(row)}>
                                            Show more →
                                        </span>
                                    </CardActions>
                                </Card>
                            </Grid>
                        );
                    })
                )}
            </Grid>

            <CommonDialog
                className="AzureOpenAIDialog"
                open={open}
                onClose={handleClose}
                title={"Bedrock Access Status"}
                dialogType={"fui"}
                dialogContent={
                    <DialogContent>
                        <DialogContentText id="alert-dialog-descreption">
                            <Typography sx={{ mt: 2, mb: 1 }} component="div">
                                <p style={{ color: "black" }}>Please find the details below:</p>

                                {[
                                    ["Inference Profile ID", modalData.inferenceProfileId],
                                    ["Inference Profile Name", modalData.inferenceProfileName],
                                    ["Inference Profile ARN", modalData.inferenceProfileArn],
                                    ["Model Name", modalData.modelName],
                                    ["Model Source", modalData.modelSource],
                                    ["AWS Account ID", modalData.awsAccountId],
                                    ["IAM ARN", modalData.iamArn],
                                    ["Client Name", modalData.clientName],
                                    ["Enterprise ID", modalData.enterpriseId],
                                    ["Primary POC", modalData.primaryPoc],
                                    ["Secondary POC", modalData.secondaryPoc],
                                    ["Region", modalData.region],
                                    ["Deployment Region", modalData.deploymentRegion],
                                    ["Environment", modalData.environment],
                                    ["Status", modalData.status],
                                    ["WBSE Code", modalData.wbseCode],
                                    ["Created Date", modalData.createdDate],
                                ].map(([label, value]) => (
                                    <div className="llm-dialog-row" key={label}>
                                        <span className="llm-dialog-label">{label}:</span>
                                        <span className="llm-dialog-value">{value}</span>
                                    </div>
                                ))}

                                {/* Sensitive fields — masked by default, reveal + copy on demand */}
                                <SecretDialogRow label="Access Key ID" value={modalData.accessKeyId} />
                                <SecretDialogRow label="Secret Access Key" value={modalData.secretAccessKey} />
                                <SecretDialogRow label="Secret ARN" value={modalData.secretArn} />

                                <div style={{ marginTop: "16px" }}>
                                    <b style={{ fontWeight: 600, color: "#3d2266" }}>
                                        Inference Profile Models <span style={{ fontSize: "18px" }}>&#128071;</span>
                                    </b>
                                    <table className="llm-model-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Model ARN</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(modalData.inferenceProfileModels || []).map((modelArn, idx) => (
                                                <tr key={idx}>
                                                    <td>{idx + 1}</td>
                                                    <td style={{ wordBreak: "break-all" }}>{modelArn}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Typography>
                        </DialogContentText>
                    </DialogContent>
                }
                dialogActions={
                    <DialogActions>
                        <PrimaryButton
                            data-cy="dialogOkButton"
                            variant="outlined"
                            onClick={handleClose}
                            label={"Close"}
                            className="llm-dialog-close-btn"
                        />
                    </DialogActions>
                }
            >
            </CommonDialog>
        </div>
    );
};

export default BedrockAccessCards;